import type { DataState } from '#UNSAFE_Collection';
import type { Item } from '#utils/UNSAFE_dataProvider';
import { useMemo } from 'preact/compat';

type Key = string | number;
type Data = Record<string, any>;

type UseCollectionRendererCommonParams<K> = {
  currentKey?: K;
  searchText?: string;
  selectedKeys?: Set<K>;
  onCurrentKeyChange: (detail: { value?: K }) => void;
};

export type UseCollectionRendererSelectSingleParams<K, D> = UseCollectionRendererCommonParams<K> & {
  onItemAction: (detail: { context: Item<K, D> }) => void;
  onSelectedKeysChange?: never;
  selectedOnlyData?: never;
};

export type UseCollectionRendererSelectMultipleParams<K, D> =
  UseCollectionRendererCommonParams<K> & {
    onItemAction?: never;
    onSelectedKeysChange: (detail: { value: Set<K> }) => void;
    selectedOnlyData?: DataState<K, D>;
  };

type UseCollectionRendererParams<K, D> =
  | UseCollectionRendererSelectSingleParams<K, D>
  | UseCollectionRendererSelectMultipleParams<K, D>;

type CommonCollectionRendererContext<K extends Key> = {
  searchText?: string;
  currentRowKeyOverride?: K;
  onPersistCurrentRowKey: (detail: { value?: K }) => void;
  selected?: Set<K>;
};

export type SelectSingleCollectionRendererContext<
  K extends Key,
  D extends Data
> = CommonCollectionRendererContext<K> & {
  onRowAction: (detail: { context: Item<K, D> }) => void;
};

export type SelectMultipleCollectionRendererContext<
  K extends Key,
  D extends Data
> = CommonCollectionRendererContext<K> & {
  onSelectedChange: (detail: { value: Set<K> }) => void;
  selectedOnlyData?: DataState<K, D>;
};

type CollectionRendererContext<K extends Key, D extends Data> =
  | SelectSingleCollectionRendererContext<K, D>
  | SelectMultipleCollectionRendererContext<K, D>;

// NOTE: We will using a single hook that constructs the collection renderer context for both select single and
// select multiple. This is ok as they both share most of the properties and differ only in a few areas. So, we will
// be using function overloading to determine which component is calling the hook (based on the args) and return the
// correct context.

/**
 * A custom hook that constructs the context for SelectSingle's collectionRenderer.
 * @param params Parameters for constructing the collection renderer context
 * @returns the CollectionRendererContext object
 */
export function useCollectionRenderer<K extends Key, D extends Data>(
  params: UseCollectionRendererSelectSingleParams<K, D>
): SelectSingleCollectionRendererContext<K, D>;

/**
 * A custom hook that constructs the context for SelectMultiple's collectionRenderer.
 * @param params Parameters for constructing the collection renderer context
 * @returns the CollectionRendererContext object
 */
export function useCollectionRenderer<K extends Key, D extends Data>(
  params: UseCollectionRendererSelectMultipleParams<K, D>
): SelectMultipleCollectionRendererContext<K, D>;

/**
 * A custom hook that constructs the context for SelectSingle/SelectMultiple's collectionRenderer.
 * @param param0 Parameters for constructing the collection renderer context
 * @returns the CollectionRendererContext object
 */
export function useCollectionRenderer<K extends Key, D extends Data>({
  currentKey,
  onCurrentKeyChange,
  onItemAction,
  onSelectedKeysChange,
  searchText,
  selectedKeys,
  selectedOnlyData
}: UseCollectionRendererParams<K, D>) {
  return useMemo<CollectionRendererContext<K, D>>(() => {
    const common = {
      searchText,
      currentRowKeyOverride: currentKey,
      onPersistCurrentRowKey: onCurrentKeyChange,
      selected: selectedKeys
    } satisfies CommonCollectionRendererContext<K>;

    // If onItemAction is present, then we are constructing context for SelectSingle. So,
    // return the context satisfying SelectSingleCollectionRendererContext type.
    if (onItemAction !== undefined)
      return {
        ...common,
        onRowAction: onItemAction
      } satisfies SelectSingleCollectionRendererContext<K, D>;

    // If onItemAction is not present, then we are constructing context for SelectMultiple. So,
    // return the context satisfying SelectMultipleCollectionRendererContext type.
    return {
      ...common,
      onSelectedChange: onSelectedKeysChange,
      selectedOnlyData
    } satisfies SelectMultipleCollectionRendererContext<K, D>;
  }, [
    currentKey,
    onCurrentKeyChange,
    onItemAction,
    onSelectedKeysChange,
    searchText,
    selectedKeys,
    selectedOnlyData
  ]);
}
